import React, { useRef, useEffect, useState } from "react";
import FooterLeft from "./FooterLeft";
import FooterRight from "./FooterRight";
import "./VideoCard.css";

const VideoCard = ({
  url,
  profilePic,
  username,
  description,
  song,
  likes,
  comments,
  saves,
  shares,
  setVideoRef,
  isMuted,
  onMuteToggle,
  volume,
  onVolumeChange,
  "data-src": dataSrc,
  shouldPreload = false,
}) => {
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [volumeControlVisible, setVolumeControlVisible] = useState(true);
  const videoRef = useRef(null);
  const volumeControlTimeoutRef = useRef(null);

  const handleVideoError = () => {
    console.error("Video failed to load:", dataSrc);
    setIsError(true);
    setIsLoading(false);
  };

  const handleVideoLoaded = () => {
    setIsLoading(false);
    setIsPreloaded(true);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    onVolumeChange(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      if (newVolume === 0 && !isMuted) {
        onMuteToggle();
      } else if (newVolume > 0 && isMuted) {
        onMuteToggle();
      }
    }
  };

  const showVolumeControls = () => {
    setVolumeControlVisible(true);
    if (volumeControlTimeoutRef.current) {
      clearTimeout(volumeControlTimeoutRef.current);
    }
    volumeControlTimeoutRef.current = setTimeout(() => {
      setVolumeControlVisible(false);
    }, 1000);
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
      setVideoRef(videoRef.current);

      // Start preloading if necessary
      if (shouldPreload && !isPreloaded && dataSrc && !videoRef.current.src) {
        videoRef.current.src = dataSrc;
        videoRef.current.load();
      }
    }

    return () => {
      // Clean up the timeout when component unmounts
      if (volumeControlTimeoutRef.current) {
        clearTimeout(volumeControlTimeoutRef.current);
      }
    };
  }, [isMuted, setVideoRef, shouldPreload, dataSrc, isPreloaded]);

  return (
    <div
      className="video"
      onMouseMove={showVolumeControls}
      onMouseEnter={showVolumeControls}
    >
      {isLoading && <div className="video-loading"> Loading... </div>}
      {isError && <div className="video-error"> Error loading video </div>}
      <video
        className="player"
        onClick={onMuteToggle}
        ref={videoRef}
        loop
        muted={isMuted}
        playsInline
        preload={shouldPreload ? "auto" : "metadata"}
        onError={handleVideoError}
        onLoadedData={handleVideoLoaded}
        data-src={dataSrc}
      ></video>

      <div
        className="mute-button"
        onClick={(e) => {
          e.stopPropagation();
          onMuteToggle();
        }}
      >
        {isMuted ? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M11 5L6 9H2V15H6L11 19V5Z" fill="white" />
            <path
              d="M23 9L17 15"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M17 9L23 15"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M11 5L6 9H2V15H6L11 19V5Z" fill="white" />
            <path
              d="M15.54 8.46C16.4774 9.39764 17.004 10.6692 17.004 11.995C17.004 13.3208 16.4774 14.5924 15.54 15.53"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.24 5.76C19.9257 7.44568 20.8841 9.67594 20.8841 12C20.8841 14.3241 19.9257 16.5543 18.24 18.24"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      {!isMuted ? (
        <div
          className={`volume-control ${!volumeControlVisible ? "hidden" : ""}`}
        >
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume} // Use the prop instead of currVolume
            onChange={handleVolumeChange}
          />
        </div>
      ) : null}

      <div className="bottom-controls">
        <div className="footer-left">
          <FooterLeft
            username={username}
            description={description}
            song={song}
          />
        </div>
        <div className="footer-right">
          <FooterRight
            likes={likes}
            shares={shares}
            comments={comments}
            saves={saves}
            profilePic={profilePic}
          />
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
